import { useAuthStore } from "@/auth/authStore";
import type { AuthSessionContext, TokenPayload } from "@/auth/sessionTypes";

/**
 * Standard error envelope returned by every backend 4xx/5xx response:
 *   { code, message, detail, request_id }
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;
  readonly requestId: string | undefined;

  constructor(opts: {
    status: number;
    code: string;
    message: string;
    detail?: unknown;
    requestId?: string;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.detail = opts.detail;
    this.requestId = opts.requestId;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * In-flight refresh promise so multiple 401s in parallel collapse into one
 * `/v1/auth/refresh` call.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const { refreshToken, setTokens, setSessionContext, clear } = useAuthStore.getState();
  if (!refreshToken) {
    clear();
    return false;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      clear();
      return false;
    }
    const json = (await res.json()) as { data: TokenPayload };
    setTokens({
      accessToken: json.data.access_token,
      refreshToken: json.data.refresh_token,
    });
    setSessionContext(buildSession(json.data));
    return true;
  } catch {
    clear();
    return false;
  }
}

function buildSession(payload: TokenPayload): AuthSessionContext {
  return (
    payload.session ?? {
      user: payload.user,
      global_roles: [],
      member_profile: null,
      journal_memberships: [],
    }
  );
}

function generateRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** When true, do not attach the bearer token (used for /auth/login etc). */
  anonymous?: boolean;
  /** When true, do not retry on 401. Used internally during refresh. */
  noRetry?: boolean;
  signal?: AbortSignal;
  /** Optional content-type override for raw bodies (e.g. presigned PUT). */
  contentType?: string;
}

function buildUrl(path: string, query?: ApiRequestOptions["query"]): string {
  const base = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export async function apiRequest<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, anonymous, noRetry, signal, contentType } = opts;
  const headers: Record<string, string> = {
    Accept: "application/json",
    "x-request-id": generateRequestId(),
  };
  if (body !== undefined) {
    headers["Content-Type"] = contentType ?? "application/json";
  }
  if (!anonymous) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  // Auto-refresh on 401 once.
  if (res.status === 401 && !anonymous && !noRetry) {
    refreshInFlight = refreshInFlight ?? refreshTokens();
    const ok = await refreshInFlight;
    refreshInFlight = null;
    if (ok) {
      return apiRequest<T>(path, { ...opts, noRetry: true });
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const parsed: unknown = text ? safeJsonParse(text) : undefined;

  if (!res.ok) {
    const envelope =
      isErrorEnvelope(parsed) ?
        parsed
      : { code: `http_${res.status}`, message: res.statusText || "Request failed" };
    throw new ApiError({
      status: res.status,
      code: envelope.code,
      message: envelope.message,
      detail: (parsed as { detail?: unknown })?.detail,
      requestId:
        (parsed as { request_id?: string })?.request_id ??
        res.headers.get("x-request-id") ??
        undefined,
    });
  }

  return parsed as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isErrorEnvelope(value: unknown): value is { code: string; message: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { code?: unknown }).code === "string" &&
    typeof (value as { message?: unknown }).message === "string"
  );
}
